import * as THREE from 'three'
import { overrideOutlineScale } from '../render/materials'
import { OUTLINE } from '../render/theme'
import { applyView, fitView, type ViewState } from '../scene/camera'
import { hideHelpers, isHelper, restoreHelpers, sceneBoxes } from '../scene/helpers'

export type ExportFraming = 'fit' | 'current'

export interface ExportOptions {
  /** Final PNG edge length in pixels. */
  size: number
  /** Render at `size * supersample` then downscale — the only way thin dark lines survive. */
  supersample?: number
  framing?: ExportFraming
  padding?: number
  transparent?: boolean
  background?: string
}

export interface ExportContext {
  gl: THREE.WebGLRenderer
  scene: THREE.Scene
  camera: THREE.OrthographicCamera
  view: ViewState
  /** Current on-screen aspect, width / height. */
  aspect: number
}

/**
 * Practical ceiling for the offscreen render, independent of what the driver
 * claims it can allocate.
 *
 * The canvas is multisampled (`antialias: true`), so a NxN drawing buffer costs
 * roughly N² × 4 bytes × sample count. At 8192² that is on the order of a
 * gigabyte of multisample storage, and drivers that cannot back it do not fail
 * loudly — they resolve garbage, which shows up as faint diagonal streaks and
 * ghosting across the image. 4096² keeps the multisample allocation inside what
 * every mainstream GPU handles, and a 2:1 downscale from it is already a clean
 * four-samples-per-output-pixel.
 */
const MAX_EXPORT_BUFFER = 4096

/**
 * Every real cap on a WebGL drawing buffer. Oversizing does not throw: the
 * driver silently allocates something smaller with no guarantee it keeps the
 * aspect ratio, which would quietly break squareness.
 */
function maxRenderSize(gl: THREE.WebGLRenderer): number {
  const context = gl.getContext()
  const maxRenderbuffer = context.getParameter(context.MAX_RENDERBUFFER_SIZE) as number
  const maxViewport = context.getParameter(context.MAX_VIEWPORT_DIMS) as Int32Array
  const maxTexture = gl.capabilities.maxTextureSize
  const iOS =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  // WebKit caps total canvas AREA, not just each dimension.
  const maxArea = iOS ? 8192 : 16384
  return Math.max(
    1,
    Math.min(
      maxRenderbuffer,
      maxViewport[0] ?? 0,
      maxViewport[1] ?? 0,
      maxTexture,
      maxArea,
      MAX_EXPORT_BUFFER,
    ),
  )
}

/** `#rgb` / `#rrggbb` to 8-bit sRGB components, with no colour-space conversion. */
function parseHexRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padEnd(6, 'f')
  const value = parseInt(full.slice(0, 6), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

interface DownscaleOptions {
  /** WebGL contexts premultiply by default; un-premultiplying is our job. */
  premultiplied: boolean
  /** Solid colour to composite over, or null to keep straight alpha. */
  backdrop: [number, number, number] | null
}

/**
 * Flips the framebuffer rows and box-averages them down to the output size, on
 * the CPU.
 *
 * `readPixels` hands back bottom-up rows, and the render is always exactly 1x or
 * 2x the requested edge, so this is an exact 2x2 average — no resampling kernel,
 * no browser filter, no dithering, bit-identical everywhere.
 */
function downscale(
  raw: Uint8Array,
  bufferWidth: number,
  bufferHeight: number,
  size: number,
  { premultiplied, backdrop }: DownscaleOptions,
): Uint8Array {
  const scale = Math.max(1, Math.round(bufferWidth / size))
  const samples = scale * scale
  const out = new Uint8Array(size * size * 4)

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let r = 0
      let g = 0
      let b = 0
      let a = 0
      for (let sy = 0; sy < scale; sy += 1) {
        const sourceY = bufferHeight - 1 - (y * scale + sy)
        if (sourceY < 0) continue
        const row = sourceY * bufferWidth * 4
        for (let sx = 0; sx < scale; sx += 1) {
          const offset = row + (x * scale + sx) * 4
          r += raw[offset] ?? 0
          g += raw[offset + 1] ?? 0
          b += raw[offset + 2] ?? 0
          a += raw[offset + 3] ?? 0
        }
      }
      // Averaging in premultiplied space is what makes edge coverage correct.
      r /= samples
      g /= samples
      b /= samples
      a /= samples

      const index = (y * size + x) * 4
      if (backdrop) {
        const coverage = a / 255
        const [br, bg, bb] = backdrop
        if (premultiplied) {
          out[index] = Math.round(r + br * (1 - coverage))
          out[index + 1] = Math.round(g + bg * (1 - coverage))
          out[index + 2] = Math.round(b + bb * (1 - coverage))
        } else {
          out[index] = Math.round(r * coverage + br * (1 - coverage))
          out[index + 1] = Math.round(g * coverage + bg * (1 - coverage))
          out[index + 2] = Math.round(b * coverage + bb * (1 - coverage))
        }
        out[index + 3] = 255
      } else {
        // PNG stores straight alpha.
        const divisor = premultiplied && a > 0 ? a / 255 : 1
        out[index] = Math.min(255, Math.round(r / divisor))
        out[index + 1] = Math.min(255, Math.round(g / divisor))
        out[index + 2] = Math.min(255, Math.round(b / divisor))
        out[index + 3] = Math.round(a)
      }
    }
  }

  return out
}

/* ------------------------------------------------------------ PNG writer */

const CRC_TABLE = /* @__PURE__ */ (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n += 1) {
    let c = n
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i += 1) {
    c = (CRC_TABLE[(c ^ (bytes[i] ?? 0)) & 0xff] ?? 0) ^ (c >>> 8)
  }
  return (c ^ 0xffffffff) >>> 0
}

function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length)
  const view = new DataView(out.buffer)
  view.setUint32(0, data.length)
  for (let i = 0; i < 4; i += 1) out[4 + i] = type.charCodeAt(i)
  out.set(data, 8)
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)))
  return out
}

/**
 * Encodes RGBA bytes as a PNG, in JavaScript.
 *
 * The browser's own encoder (`canvas.toBlob`) is the last colour-managed step in
 * the chain: an accelerated 2D canvas can be backed in the display's colour
 * space, so `putImageData` converts on the way in and `toBlob` writes a profile
 * on the way out. Both are invisible locally and produce, on a wide-gamut
 * monitor, a dithered patchwork across flat surfaces. Writing the file here
 * means the bytes measured in memory are literally the bytes on disk, on every
 * machine.
 */
async function encodePng(rgba: Uint8Array, size: number): Promise<Blob> {
  const stride = size * 4
  // PNG scanlines are each prefixed with a filter byte; 0 = None.
  const scanlines = new Uint8Array((stride + 1) * size)
  for (let y = 0; y < size; y += 1) {
    scanlines.set(rgba.subarray(y * stride, (y + 1) * stride), y * (stride + 1) + 1)
  }

  // CompressionStream('deflate') emits zlib-wrapped deflate, which is exactly
  // what an IDAT stream must contain.
  const compressed = new Uint8Array(
    await new Response(
      new Blob([scanlines as unknown as BlobPart]).stream().pipeThrough(new CompressionStream('deflate')),
    ).arrayBuffer(),
  )

  const header = new Uint8Array(13)
  const headerView = new DataView(header.buffer)
  headerView.setUint32(0, size)
  headerView.setUint32(4, size)
  header[8] = 8 // bit depth
  header[9] = 6 // colour type: truecolour with alpha
  header[10] = 0 // deflate
  header[11] = 0 // adaptive filtering
  header[12] = 0 // no interlace

  return new Blob(
    [
      new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
      pngChunk('IHDR', header),
      pngChunk('IDAT', compressed),
      pngChunk('IEND', new Uint8Array(0)),
    ] as unknown as BlobPart[],
    { type: 'image/png' },
  )
}

/**
 * Renders a square PNG.
 *
 * Contract: render and read back inside ONE synchronous block. There is no
 * `preserveDrawingBuffer` (the spec endorses same-task access and warns the flag
 * costs performance), so any `await` between `gl.render` and the read would come
 * back transparent. Encoding happens afterwards, off a 2D canvas that keeps its
 * bitmap indefinitely.
 */
export async function exportSquarePng(context: ExportContext, options: ExportOptions): Promise<Blob> {
  const { gl, scene, camera, view, aspect } = context
  const supersample = options.supersample ?? 2
  const framing = options.framing ?? 'fit'
  const cap = maxRenderSize(gl)
  const renderSize = Math.max(1, Math.min(Math.round(options.size * supersample), cap))

  // --- Square framing, in world units -------------------------------------
  let exportView: ViewState = { ...view }
  if (framing === 'fit') {
    const boxes = sceneBoxes(scene, isHelper)
    const fit = fitView(boxes, 1, view.mode, view.viewIndex, options.padding ?? 1.05)
    if (fit) exportView = { ...view, ...fit }
  } else {
    // Keep everything that was on screen: grow the short axis, never crop.
    exportView = { ...view, halfY: view.halfY * Math.max(1, aspect) }
  }

  // --- Snapshot every piece of state we touch ------------------------------
  const previousPixelRatio = gl.getPixelRatio()
  const previousSize = gl.getSize(new THREE.Vector2())
  const previousBackground = scene.background
  const previousClearColor = gl.getClearColor(new THREE.Color())
  const previousClearAlpha = gl.getClearAlpha()
  const previousCamera = {
    left: camera.left,
    right: camera.right,
    top: camera.top,
    bottom: camera.bottom,
    position: camera.position.clone(),
    quaternion: camera.quaternion.clone(),
  }

  /*
   * Fat lines are sized in pixels via `linewidth / resolution`, and the shader
   * derives its aspect correction from `resolution.x / resolution.y`. Leaving a
   * 16:9 resolution in place while rendering a square buffer would make the ink
   * anisotropic — horizontal edges thicker than vertical ones. So the resolution
   * is squared to match the buffer and the width is rescaled against a fixed
   * reference height, which also makes the exported line weight independent of
   * whatever size the user's browser window happened to be.
   */
  const restoreOutlines = overrideOutlineScale(renderSize, renderSize / OUTLINE.referenceHeight)

  const hidden = hideHelpers(scene)

  if (options.transparent) {
    scene.background = null
    gl.setClearColor(0x000000, 0)
  } else if (options.background) {
    scene.background = new THREE.Color(options.background)
  }

  let pixels: Uint8Array | null = null

  try {
    applyView(camera, exportView, 1)
    gl.setPixelRatio(1)
    gl.setSize(renderSize, renderSize, false)

    /*
     * Resizing reallocates the drawing buffer (and its multisample storage).
     * Some drivers hand back the first frame after that reallocation before the
     * multisample resolve has fully landed, which reads back as faint diagonal
     * streaks — a ghost of whatever was in the buffer before. Clearing
     * explicitly and throwing away one warm-up frame costs a few milliseconds
     * once per export and makes the readback deterministic.
     */
    gl.clear(true, true, true)
    gl.render(scene, camera)
    gl.clear(true, true, true)
    gl.render(scene, camera)

    const buffer = gl.getContext()
    if (buffer.drawingBufferWidth !== renderSize || buffer.drawingBufferHeight !== renderSize) {
      console.warn(
        `[export] driver shrank the buffer to ${buffer.drawingBufferWidth}x${buffer.drawingBufferHeight}`,
      )
    }

    /*
     * Read the framebuffer bytes directly instead of `drawImage`-ing the WebGL
     * canvas into a 2D one.
     *
     * That hop is a colour-managed operation: whenever the two canvases resolve
     * to different colour spaces — which happens by default on wide-gamut and
     * HDR displays — the browser performs a DITHERED gamut conversion. On flat
     * near-white artwork the dither is not noise, it is long diagonal streaks,
     * and it turns neutral greys into slightly chromatic ones (a #fafafa floor
     * comes out as a patchwork of #f9faf8 / #fdf7fc / #fbfcfa). `readPixels`
     * plus `putImageData` moves raw bytes with no conversion anywhere, so the
     * PNG is identical on every machine.
     */
    const bufferWidth = buffer.drawingBufferWidth
    const bufferHeight = buffer.drawingBufferHeight
    const raw = new Uint8Array(bufferWidth * bufferHeight * 4)
    gl.getContext().readPixels(
      0,
      0,
      bufferWidth,
      bufferHeight,
      buffer.RGBA,
      buffer.UNSIGNED_BYTE,
      raw,
    )

    const premultiplied = gl.getContext().getContextAttributes()?.premultipliedAlpha !== false
    const backdrop = options.transparent ? null : parseHexRgb(options.background ?? '#ffffff')

    pixels = downscale(raw, bufferWidth, bufferHeight, options.size, { premultiplied, backdrop })
  } finally {
    restoreHelpers(hidden)
    restoreOutlines()
    scene.background = previousBackground
    gl.setClearColor(previousClearColor, previousClearAlpha)
    camera.left = previousCamera.left
    camera.right = previousCamera.right
    camera.top = previousCamera.top
    camera.bottom = previousCamera.bottom
    camera.position.copy(previousCamera.position)
    camera.quaternion.copy(previousCamera.quaternion)
    camera.updateProjectionMatrix()
    camera.updateMatrixWorld()
    // setPixelRatio internally calls setSize, so it has to come first.
    gl.setPixelRatio(previousPixelRatio)
    gl.setSize(previousSize.x, previousSize.y, false)
    gl.render(scene, camera)
  }

  if (!pixels) throw new Error('No se pudo leer el búfer de render')
  return encodePng(pixels, options.size)
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Revoking synchronously aborts the download in Firefox and Edge.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export function slugify(value: string): string {
  return (
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'espacio'
  )
}
